import type { RecordingQuality } from "../context/RecorderContext";

// Base quality tiers - these represent the vertical resolution for common aspect ratios
const QUALITY_TIERS: Record<RecordingQuality, number> = {
  "720p": 720,
  "1080p": 1080,
  "2k": 1440, // QHD (1440p)
};

// Supported aspect ratios with their width multipliers
const ASPECT_RATIOS: Record<string, number> = {
  "16:9": 16/9,
  "1:1": 1,
  "9:16": 9/16, // Portrait
};

export function resSize(quality: RecordingQuality, ratioStr: string) {
  const [rw, rh] = ratioStr.split(":").map(Number);
  
  // Calculate the aspect ratio
  let aspectRatio: number;
  if (rw > 0 && rh > 0) {
    aspectRatio = rw / rh;
  } else {
    // Default to 16:9 if invalid ratio
    aspectRatio = ASPECT_RATIOS["16:9"] || 16/9;
  }

  // Get the base height from quality tier
  const baseHeight = QUALITY_TIERS[quality] || QUALITY_TIERS["1080p"];
  
  // For landscape orientations (width > height), e.g., 16:9
  if (aspectRatio >= 1) {
    const width = Math.round(baseHeight * aspectRatio);
    // Ensure width is even (some encoders prefer even dimensions)
    return { width: width % 2 === 0 ? width : width + 1, height: baseHeight };
  }
  // For portrait orientations (height > width), e.g., 9:16
  // For portrait, the width is the short edge (baseHeight) and height is the long edge
  else {
    const width = baseHeight; // 720 for 720p
    const height = Math.round(baseHeight / aspectRatio); // 720 / 0.5625 = 1280
    // Ensure both dimensions are even
    const adjustedWidth = width % 2 === 0 ? width : width + 1;
    const adjustedHeight = height % 2 === 0 ? height : height + 1;
    return { width: adjustedWidth, height: adjustedHeight };
  }
}

// Helper function to get display dimensions (for UI elements that need to scale)
export function getDisplaySize(quality: RecordingQuality, ratioStr: string, referenceWidth: number) {
  const { width: targetWidth } = resSize(quality, ratioStr);
  const scale = referenceWidth / targetWidth;
  return { scale, targetWidth };
}

// Direct scale calculation from wrapper and canvas dimensions
// Use this instead of getDisplaySize when you have actual canvas dimensions
export function getDisplayScale(wrapperWidth: number, canvasWidth: number): number {
  if (!wrapperWidth || !canvasWidth) return 1;
  return wrapperWidth / canvasWidth;
}